import React, {useEffect, useState} from 'react';
import {usePlugins} from './usePlugins';
import {ProvidedGlobalProps, withGlobalProps} from '../../providers/globalPropsProvider';
import {APP_DEFINITION_ID} from '@wix/wixstores-client-core/dist/es/src/constants';
import {getRicoRichContent} from './utils';
import type {BISettings, CustomTextualStyle, WixRicosTheme} from '@wix/ricos';
import {WixRicosViewer, fromCKEditorHtml, modify, Node_Type, RichContent, TextStyle_TextAlignment} from '@wix/ricos';
import '@wix/ricos/css/ricos-viewer.global.css';
import {ISettingsColor, ISettingsFont} from '@wix/tpa-settings';

export type RichContentViewerProps = ProvidedGlobalProps & {
  contentAsString: string;
  ricosContext: string;
  dataHook: string;
  stylesParams: {
    textColor: ISettingsColor;
    textFont: ISettingsFont;
    alignment: ISettingsFont;
    table?: {
      BackgroundColor: ISettingsColor;
      borderColor: ISettingsColor;
      evenNumberColor: ISettingsColor;
    };
  };
  onLoad?(): any;
};

const getTableStyles = (ricoDescription: RichContent, stylesParams: RichContentViewerProps['stylesParams']) => {
  const borderColor = stylesParams.table?.borderColor?.value;
  const backgroundColor = stylesParams.table?.BackgroundColor?.value;
  const evenNumberBackgroundColor = stylesParams.table?.evenNumberColor?.value;
  ricoDescription = modify(ricoDescription)
    .filter((n) => {
      return n.type === Node_Type.TABLE_CELL;
    })
    .set((n: any) => {
      return {
        ...n,
        tableCellData: {
          borderColors: {
            top: borderColor,
            right: borderColor,
            bottom: borderColor,
            left: borderColor,
          },
          cellStyle: {
            backgroundColor,
          },
        },
      };
    });

  let index: number;
  let isEven: boolean;
  ricoDescription = modify(ricoDescription)
    .filter((n) => {
      switch (n.type) {
        case Node_Type.TABLE:
          index = 0;
          isEven = true;
          break;
        case Node_Type.TABLE_ROW:
          isEven = index % 2 === 0;
          index++;
          break;
        case Node_Type.TABLE_CELL:
          return isEven;
        default:
          return false;
      }
      return false;
    })
    .set((n: any) => {
      return {
        ...n,
        tableCellData: {
          ...n.tableCellData,
          cellStyle: {
            backgroundColor: evenNumberBackgroundColor,
          },
        },
      };
    });
  return ricoDescription;
};

const getAlimentContent = (ricoDescription: RichContent, styleParams: RichContentViewerProps['stylesParams']) => {
  const alignment = styleParams.alignment.value.toUpperCase() as TextStyle_TextAlignment;

  return modify(ricoDescription).set((n: any) => {
    switch (n?.type) {
      case Node_Type.GALLERY:
        return {
          ...n,
          galleryData: {
            ...n.galleryData,
            containerData: {
              ...n.galleryData?.containerData,
              alignment,
            },
          },
        };
      case Node_Type.IMAGE:
        return {
          ...n,
          imageData: {
            ...n.imageData,
            containerData: {
              ...n.imageData?.containerData,
              alignment,
            },
          },
        };
      case Node_Type.GIF:
        return {
          ...n,
          gifData: {
            ...n.gifData,
            containerData: {
              ...n.gifData?.containerData,
              alignment,
            },
          },
        };
      case Node_Type.FILE:
        return {
          ...n,
          fileData: {
            ...n.fileData,
            containerData: {
              ...n.fileData?.containerData,
              alignment,
            },
          },
        };
      case Node_Type.CODE_BLOCK:
        return {
          ...n,
          codeBlockData: {
            ...n.codeBlockData,
            textStyle: {
              ...n.codeBlockData?.textStyle,
              textAlignment: alignment,
            },
          },
        };
      case Node_Type.PARAGRAPH:
        return {
          ...n,
          paragraphData: {
            ...(n.paragraphData || {}),
            textStyle: {
              ...(n.paragraphData?.textStyle || {}),
              textAlignment: alignment,
            },
          },
        };
      case Node_Type.DIVIDER:
        return {
          ...n,
          dividerData: {
            ...n.dividerData,
            alignment,
            containerData: {
              ...n.dividerData?.containerData,
              alignment,
            },
          },
        };
      default:
        return n;
    }
  });
};

export const RichContentViewer: React.FC<RichContentViewerProps> = withGlobalProps(
  ({contentAsString, ricosContext, globals, dataHook, onLoad, stylesParams}: RichContentViewerProps) => {
    const {isEditorMode, instanceId} = globals;
    const [isLoaded, setIsLoaded] = useState(false);
    const [ref, setRef] = useState<HTMLDivElement | null>(null);
    const {textFont, textColor, alignment} = stylesParams;

    const biSettings: BISettings = {
      consumer: 'Online Stores - Product Page',
      platform: isEditorMode ? 'Editor' : 'Livesite',
      postId: '',
      containerId: APP_DEFINITION_ID,
      usage: ricosContext,
    };

    useEffect(() => {
      if (isLoaded) {
        return;
      }

      if (ref?.childNodes.length > 0) {
        setIsLoaded(true);
        onLoad?.();
      }
    }, [ref, isLoaded, onLoad]);

    let ricoDescription = getRicoRichContent(contentAsString);
    /* istanbul ignore next: tested in sled */
    if (!ricoDescription) {
      ricoDescription = fromCKEditorHtml(contentAsString);
    }

    /* istanbul ignore file */
    const customStyles: CustomTextualStyle = {
      ...(textFont.style?.bold && {fontWeight: 'bold'}),
      ...(textFont.style?.italic && {fontStyle: 'italic'}),
      ...(textFont.style?.underline && {textDecoration: 'underline'}),
      fontSize: `${textFont.size}px`,
    };
    const theme: WixRicosTheme = {
      palette: {
        textColor: textColor.value,
      },
      typography: {
        fontFamily: textFont.family,
      },
      customStyles: {
        p: customStyles,
        h1: customStyles,
        h2: customStyles,
        h3: customStyles,
        h4: customStyles,
        h5: customStyles,
        h6: customStyles,
        link: customStyles,
        hashtag: customStyles,
        quote: customStyles,
      },
    };

    if (alignment?.value) {
      ricoDescription = getAlimentContent(ricoDescription, stylesParams);
    }

    if (
      stylesParams.table?.BackgroundColor?.value ||
      stylesParams.table?.evenNumberColor?.value ||
      stylesParams.table?.borderColor?.value
    ) {
      ricoDescription = getTableStyles(ricoDescription, stylesParams);
    }

    const isBrowser = typeof document !== 'undefined';
    const mediaSettings = /* istanbul ignore next */ isBrowser ? {fullscreenProps: {container: document?.body}} : {};

    return (
      <div data-hook={dataHook} ref={(r) => setRef(r)}>
        <WixRicosViewer
          theme={theme}
          biSettings={biSettings}
          mediaSettings={mediaSettings}
          content={ricoDescription}
          plugins={usePlugins()}
          instance={instanceId}
        />
      </div>
    );
  }
);
